import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { MessageSquare, Edit2, X, Trash2, Copy, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@caruso/ui';
import { Button } from '@caruso/ui';

const AVAILABLE_VARIABLES = [
  { key: 'nome', label: 'Nome', description: 'Nome do cliente' },
  { key: 'email', label: 'Email', description: 'Email do cliente' },
  { key: 'telefone', label: 'Telefone', description: 'Telefone do cliente' },
  { key: 'documento', label: 'Documento', description: 'CPF/CNPJ do cliente' },
  { key: 'endereco', label: 'Endereço', description: 'Endereço completo' },
  { key: 'cidade', label: 'Cidade', description: 'Cidade do cliente' },
  { key: 'estado', label: 'Estado', description: 'Estado/UF do cliente' },
  { key: 'cep', label: 'CEP', description: 'CEP do cliente' },
];

export const MessageNode = memo(({ data, id }: NodeProps) => {
  const { deleteElements } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState(data.message || '');
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  const handleSave = () => {
    data.message = message;
    setIsEditing(false);
  };

  const handleCancel = () => {
    setMessage(data.message || '');
    setIsEditing(false);
  };

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const insertVariable = (varKey: string) => {
    const variable = `{{${varKey}}}`;
    setMessage(prev => prev + variable);
    setCopiedVar(varKey);
    setTimeout(() => setCopiedVar(null), 1500);
  };

  return (
    <>
      <div className="bg-neutral-900 border-2 border-brand-gold/40 rounded-lg p-4 min-w-[250px] shadow-lg relative">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-brand-gold" />

        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-brand-gold" />
          <span className="font-bold text-white">Mensagem</span>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-brand-gold hover:text-white transition-colors p-1"
              title="Editar mensagem"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="text-brand-ivory/80 text-sm whitespace-pre-wrap">
          {message || 'Clique no ícone para editar'}
        </div>

        <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-brand-gold" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent
          className="bg-neutral-900 border-brand-gold/20 text-white max-h-[90vh] overflow-hidden flex flex-col"
          style={{ width: '90vw', maxWidth: '1400px', minWidth: '1200px' }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-brand-gold" />
              Mensagem
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Digite o texto da mensagem que será enviada. Use variáveis para personalizar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto pr-2">
            {/* Info Box */}
            <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>💡 Dica:</strong> Use variáveis no formato <code className="px-1.5 py-0.5 bg-black/30 rounded text-blue-300">{'{{variavel}}'}</code> para personalizar a mensagem com dados do cliente.
              </p>
            </div>

            <div className="grid grid-cols-[2fr_1fr] gap-6 min-h-0 w-full">
              {/* Textarea */}
              <div className="space-y-2 flex flex-col min-h-[500px]">
                <label className="block text-sm font-medium text-brand-ivory">
                  Mensagem
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="flex-1 w-full p-4 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold/50 font-mono text-base"
                  placeholder="Olá {{nome}}! Seja bem-vindo."
                  autoFocus
                />
              </div>

              {/* Variables Panel */}
              <div className="space-y-2 flex flex-col min-h-[500px]">
                <label className="block text-sm font-medium text-brand-ivory">
                  Variáveis Disponíveis
                </label>
                <div className="flex-1 bg-neutral-800/50 border border-neutral-700 rounded-lg p-3 space-y-2 overflow-y-auto">
                  {AVAILABLE_VARIABLES.map((variable) => (
                    <button
                      key={variable.key}
                      onClick={() => insertVariable(variable.key)}
                      className="w-full flex items-center justify-between gap-2 p-2 bg-neutral-900/50 hover:bg-neutral-900 border border-neutral-700 hover:border-brand-gold/50 rounded transition-colors text-left group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {variable.label}
                        </div>
                        <div className="text-xs text-neutral-400 truncate">
                          {variable.description}
                        </div>
                        <code className="text-xs text-brand-gold/80 font-mono">
                          {'{{' + variable.key + '}}'}
                        </code>
                      </div>
                      <div className="flex-shrink-0">
                        {copiedVar === variable.key ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-neutral-500 group-hover:text-brand-gold transition-colors" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-700 flex-shrink-0 mt-4">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="border-neutral-700 text-white hover:bg-neutral-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink font-semibold"
            >
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

MessageNode.displayName = 'MessageNode';
